import { useState } from 'react';
import Api, { ApiError } from '../../../../data/api/Api';
import { UserDetails } from '../../../../components/rightPanel/subpanel/developerKey/DeveloperKey';
import { showSnackbarErrorMessage } from '../../../../components/staticComponents/staticUtiles';

const UseAssistantSharedUsers = (sharedUsersIds: string[]) => {
  const [sharedUsers, setSharedUsers] = useState<UserDetails[]>([]);
  const [currentSharedUsersBatch, setCurrentSharedUsersBatch] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMoreSharedUsers, setHasMoreSharedUsers] = useState<boolean>(false);
  const sharedUsersBatchSize = 100; // Number of sharedUsers to load per batch

  const loadSharedUsersBatch = async (batchIndex: number): Promise<void> => {
    try {
      setIsLoading(true);

      // Calculate start and end indices for the batch
      const startIndex = batchIndex * sharedUsersBatchSize;
      const endIndex = Math.min(startIndex + sharedUsersBatchSize, sharedUsersIds.length);

      // Get the batch of shared user IDs
      const batchSharedUserIds = sharedUsersIds.slice(startIndex, endIndex);

      // Skip API call if there are no sharedUsers to load
      if (batchSharedUserIds.length === 0) {
        setHasMoreSharedUsers(false);
        return;
      }

      // Fetch the shared user details for this batch
      const newSharedUsersBatchDetails = (await Api.getCompleteUserDetailsByID(batchSharedUserIds)) as UserDetails[];

      // Update state with new sharedUsers (append to existing if not first batch)
      if (batchIndex === 0) {
        setSharedUsers(newSharedUsersBatchDetails);
      } else {
        setSharedUsers((prev) => [...prev, ...newSharedUsersBatchDetails]);
      }

      // Update the current batch index
      setCurrentSharedUsersBatch(batchIndex);

      // Check if there are more sharedUsers to load
      setHasMoreSharedUsers(endIndex < sharedUsersIds.length);
    } catch (error) {
      showSnackbarErrorMessage(error as ApiError);
      console.error('Error loading user batch:', error);
    } finally {
      setIsLoading(false);
    }
  };

  //Loads the initial batch (first batch) of sharedUsers
  const loadInitialSharedUsersBatch = async (): Promise<void> => {
    await loadSharedUsersBatch(0);
  };

  //Loads the next batch of sharedUsers
  const loadNextSharedUsersBatch = async (): Promise<void> => {
    await loadSharedUsersBatch(currentSharedUsersBatch + 1);
  };

  return {
    sharedUsers,
    isLoading,
    hasMoreSharedUsers,
    loadInitialSharedUsersBatch,
    loadNextSharedUsersBatch,
  };
};

export default UseAssistantSharedUsers;
